/**
 * GRI Question Input Component
 *
 * Dynamic input component that renders the appropriate form control
 * based on question type. Supports auto-save with debounce.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  TextField,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Select,
  MenuItem,
  Checkbox,
  Radio,
  RadioGroup,
  InputAdornment,
  Typography,
  Chip,
  OutlinedInput,
  SelectChangeEvent,
} from '@mui/material';
import {
  GRIQuestion,
  GRIResponseValue,
  QuestionType,
} from '../../services/griService';

interface GRIQuestionInputProps {
  question: GRIQuestion;
  value?: GRIResponseValue;
  onChange: (questionId: string, value: Partial<GRIResponseValue>) => void;
  disabled?: boolean;
  autoSaveDelay?: number;
}

const DEBOUNCE_DELAY = 1500; // 1.5 seconds

export const GRIQuestionInput: React.FC<GRIQuestionInputProps> = ({
  question,
  value,
  onChange,
  disabled = false,
  autoSaveDelay = DEBOUNCE_DELAY,
}) => {
  // Local state for immediate UI updates
  const [localValue, setLocalValue] = useState<string | number | boolean | string[] | null>(
    getInitialValue(question, value)
  );

  // Debounce timer ref
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // Update local value when prop changes
  useEffect(() => {
    setLocalValue(getInitialValue(question, value));
  }, [question.id, value]);

  // Debounced save handler
  const debouncedSave = useCallback(
    (newValue: Partial<GRIResponseValue>) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        onChange(question.id, newValue);
      }, autoSaveDelay);
    },
    [question.id, onChange, autoSaveDelay]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // Handle text/textarea changes
  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    debouncedSave({ text_value: newValue || undefined });
  };

  // Handle number changes
  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    const numValue = newValue ? parseFloat(newValue) : undefined;
    debouncedSave({ number_value: numValue });
  };

  // Handle boolean changes
  const handleBooleanChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value === 'true';
    setLocalValue(newValue);
    onChange(question.id, { boolean_value: newValue }); // Immediate save for boolean
  };

  // Handle select changes
  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    onChange(question.id, { text_value: newValue || undefined }); // Immediate save for select
  };

  // Handle multiselect changes
  const handleMultiselectChange = (e: SelectChangeEvent<string[]>) => {
    const newValue = e.target.value as string[];
    setLocalValue(newValue);
    onChange(question.id, { json_value: newValue.length > 0 ? newValue : undefined }); // Immediate save
  };

  // Handle date changes (using text input with type="date")
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    onChange(question.id, { date_value: newValue || undefined });
  };

  // Render based on question type
  const renderInput = () => {
    switch (question.question_type as QuestionType) {
      case 'text':
        return (
          <TextField
            fullWidth
            value={localValue || ''}
            onChange={handleTextChange}
            disabled={disabled}
            placeholder={question.placeholder}
            helperText={question.help_text}
            InputProps={
              question.unit
                ? {
                    endAdornment: (
                      <InputAdornment position="end">{question.unit}</InputAdornment>
                    ),
                  }
                : undefined
            }
            size="small"
          />
        );

      case 'textarea':
        return (
          <TextField
            fullWidth
            multiline
            rows={4}
            value={localValue || ''}
            onChange={handleTextChange}
            disabled={disabled}
            placeholder={question.placeholder}
            helperText={question.help_text}
            size="small"
          />
        );

      case 'number':
      case 'percentage':
        return (
          <TextField
            fullWidth
            type="number"
            value={localValue ?? ''}
            onChange={handleNumberChange}
            disabled={disabled}
            placeholder={question.placeholder}
            helperText={question.help_text}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {question.question_type === 'percentage' ? '%' : question.unit || ''}
                </InputAdornment>
              ),
            }}
            inputProps={{
              min: question.validation_rules?.min as number,
              max: question.validation_rules?.max as number,
              step: 'any',
            }}
            size="small"
          />
        );

      case 'boolean':
        return (
          <FormControl component="fieldset" disabled={disabled}>
            <RadioGroup
              row
              value={localValue === true ? 'true' : localValue === false ? 'false' : ''}
              onChange={handleBooleanChange}
            >
              <FormControlLabel value="true" control={<Radio size="small" />} label="Yes" />
              <FormControlLabel value="false" control={<Radio size="small" />} label="No" />
            </RadioGroup>
            {question.help_text && (
              <FormHelperText>{question.help_text}</FormHelperText>
            )}
          </FormControl>
        );

      case 'select':
        return (
          <FormControl fullWidth size="small" disabled={disabled}>
            <Select
              value={(localValue as string) || ''}
              onChange={handleSelectChange}
              displayEmpty
            >
              <MenuItem value="">
                <em>Select an option</em>
              </MenuItem>
              {question.options?.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {question.help_text && (
              <FormHelperText>{question.help_text}</FormHelperText>
            )}
          </FormControl>
        );

      case 'multiselect':
        return (
          <FormControl fullWidth size="small" disabled={disabled}>
            <Select
              multiple
              value={(localValue as string[]) || []}
              onChange={handleMultiselectChange}
              input={<OutlinedInput />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((val) => {
                    const option = question.options?.find((o) => o.value === val);
                    return <Chip key={val} label={option?.label || val} size="small" />;
                  })}
                </Box>
              )}
            >
              {question.options?.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Checkbox
                    checked={((localValue as string[]) || []).includes(option.value)}
                    size="small"
                  />
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {question.help_text && (
              <FormHelperText>{question.help_text}</FormHelperText>
            )}
          </FormControl>
        );

      case 'date':
        return (
          <TextField
            fullWidth
            type="date"
            value={localValue ? String(localValue).split('T')[0] : ''}
            onChange={handleDateChange}
            disabled={disabled}
            helperText={question.help_text}
            size="small"
            InputLabelProps={{
              shrink: true,
            }}
          />
        );

      case 'file':
        return (
          <Box>
            <Typography variant="body2" color="text.secondary">
              File upload coming soon
            </Typography>
            {question.help_text && (
              <FormHelperText>{question.help_text}</FormHelperText>
            )}
          </Box>
        );

      case 'table':
        return (
          <TextField
            fullWidth
            multiline
            rows={6}
            value={
              typeof localValue === 'string'
                ? localValue
                : localValue
                ? JSON.stringify(localValue, null, 2)
                : ''
            }
            onChange={handleTextChange}
            disabled={disabled}
            placeholder="Enter structured data (JSON format)"
            helperText={question.help_text || 'Enter data in JSON format'}
            size="small"
          />
        );

      default:
        return (
          <TextField
            fullWidth
            value={localValue || ''}
            onChange={handleTextChange}
            disabled={disabled}
            placeholder={question.placeholder}
            helperText={question.help_text}
            size="small"
          />
        );
    }
  };

  return (
    <Box>
      <Typography
        variant="body2"
        sx={{
          mb: 1,
          fontWeight: question.is_required ? 500 : 400,
          color: '#374151',
        }}
      >
        {question.question_text}
        {question.is_required && (
          <Typography component="span" sx={{ color: 'error.main', ml: 0.5 }}>
            *
          </Typography>
        )}
      </Typography>
      {renderInput()}
    </Box>
  );
};

// Helper function to get initial value from response
function getInitialValue(
  question: GRIQuestion,
  value?: GRIResponseValue
): string | number | boolean | string[] | null {
  if (!value) return null;

  switch (question.question_type as QuestionType) {
    case 'text':
    case 'textarea':
      return value.text_value || null;
    case 'number':
    case 'percentage':
      return value.number_value ?? null;
    case 'boolean':
      return value.boolean_value ?? null;
    case 'select':
      return value.text_value || null;
    case 'multiselect':
      return (value.json_value as string[]) || [];
    case 'date':
      return value.date_value || null;
    case 'table':
      return value.json_value ? JSON.stringify(value.json_value, null, 2) : null;
    default:
      return value.text_value || null;
  }
}

export default GRIQuestionInput;
